import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Scroll-snap carousel hook for horizontal swipeable lists.
 * Desktop: container is a CSS grid (no scroll) — hook is idle, just no-ops.
 * Mobile: container is a horizontal flex with snap-center on each item.
 */
export default function useSnapCarousel(count, initial = 0) {
  const scrollRef = useRef(null);
  const itemRefs = useRef([]);
  const [active, setActive] = useState(initial);

  const setRef = useCallback(
    (i) => (el) => {
      itemRefs.current[i] = el;
    },
    []
  );

  const scrollTo = useCallback((i) => {
    const c = scrollRef.current;
    const el = itemRefs.current[i];
    if (!c || !el) return;
    const target = el.offsetLeft - (c.clientWidth - el.clientWidth) / 2;
    c.scrollTo({ left: target, behavior: "smooth" });
  }, []);

  // Center initial item on mount (mobile only — skip if items fit without scroll)
  useEffect(() => {
    const c = scrollRef.current;
    const el = itemRefs.current[initial];
    if (!c || !el) return;
    // If container doesn't actually need to scroll (e.g. desktop grid), skip.
    if (c.scrollWidth <= c.clientWidth + 4) return;
    c.scrollLeft = el.offsetLeft - (c.clientWidth - el.clientWidth) / 2;
  }, [initial, count]);

  // Track active index via scroll position
  useEffect(() => {
    const c = scrollRef.current;
    if (!c) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const center = c.scrollLeft + c.clientWidth / 2;
        let best = 0;
        let min = Infinity;
        for (let i = 0; i < itemRefs.current.length; i++) {
          const el = itemRefs.current[i];
          if (!el) continue;
          const mid = el.offsetLeft + el.clientWidth / 2;
          const d = Math.abs(mid - center);
          if (d < min) {
            min = d;
            best = i;
          }
        }
        setActive(best);
      });
    };
    c.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      c.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  return { scrollRef, setRef, active, scrollTo };
}
